import { CSSProperties } from 'react';
import { ROUTES } from 'constant';
import parse from 'html-react-parser';
import NextImage from 'next/image';

import { Link } from '@chakra-ui/next-js';
import {
  Box,
  Button,
  Heading,
  Stack,
  Text,
  useBreakpointValue,
  useTheme
} from '@chakra-ui/react';

import { ShopItemInterface } from 'types';

import { Divider } from 'ui';

const imageStyles: CSSProperties = {
  borderRadius: '6px',
  objectFit: 'contain',
  width: '100%'
};

interface ShopItemProps {
  item: ShopItemInterface;
  onClick: () => void;
  isImageClick?: boolean;
  isShopBtnVisible?: boolean;
  mobileItemScale?: number;
}

const ShopItem = ({
  item,
  onClick,
  isImageClick = false,
  isShopBtnVisible = true,
  mobileItemScale = 1
}: ShopItemProps) => {
  const {
    colors: {
      palette: {
        shop: { item: itemColors }
      }
    }
  } = useTheme();
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' }
  );

  const defaultBaseHeight = item?.mobileBaseHeight ?? 110;
  const defaultSmHeight = item?.mobileSmHeight ?? 140;

  const imageHeight = useBreakpointValue({
    base: isImageClick ? defaultBaseHeight : 208,
    sm: isImageClick ? defaultSmHeight : 270,
    md: 336,
    lg: 350,
    xl: 456
  });

  const scale = useBreakpointValue({
    base: mobileItemScale,
    md: 1
  });

  const { img, image, title, description, linkUrl } = item;
  const staticImageSrc = isMobile ? image?.mobile : image?.desktop;
  const imgUrl = staticImageSrc || img;

  const renderImage = () =>
    (image || img) && (
      <Box
        position='relative'
        height={imageHeight}
        overflow='hidden'
        borderRadius='6px'
        display='flex'
        justifyContent='center'
        _hover={{
          shadow: 'lg'
        }}
      >
        <NextImage
          src={imgUrl}
          alt={`shop-item-${title}`}
          fill
          priority
          sizes='(max-width: 450px) 300px, 500px'
          style={{
            ...imageStyles,
            transform: `scale(${scale})`
          }}
        />
      </Box>
    );

  return (
    <Stack
      justifyContent='space-between'
      alignItems={{ base: 'center', lg: 'flex-start' }}
      gap={0}
      width='full'
    >
      <Divider
        display={{ base: 'block', md: 'none' }}
        bg={itemColors.divider}
        mb='40px'
      />

      <Stack w='full'>
        <Link
          as={Button}
          href={linkUrl || `${ROUTES.packagesShop}/${item.pkg_label_id}`}
          onClick={onClick}
          variant='outline'
          height='100%'
          width='100%'
          isExternal={item.isExternal}
          cursor='pointer'
        >
          {renderImage()}
        </Link>

        <Stack justifyContent='space-between' gap='30px'>
          {title && (
            <Heading
              as='h3'
              variant='h3'
              fontWeight='regular'
              color={itemColors.heading}
              fontSize={[22, 24, 29]}
              textAlign='center'
              mt={{ base: 0, md: '30px' }}
            >
              {title}
            </Heading>
          )}

          {description && (
            <Text
              color={itemColors.text}
              fontSize={{ base: '14px', md: '18px' }}
              lineHeight='160%'
              textAlign={{ base: 'center', lg: 'left' }}
              px={{ base: '16px', md: 0 }}
              whiteSpace='pre-wrap'
            >
              {parse(description ?? '')}
            </Text>
          )}
        </Stack>
      </Stack>

      {(!isImageClick || isShopBtnVisible) && (
        <Button
          as={Link}
          href={linkUrl || `${ROUTES.packagesShop}/${item.pkg_label_id}`}
          onClick={onClick}
          variant='outline'
          colorScheme='black'
          width='180px'
          mx='auto'
          mt={{ base: '40px', lg: '50px' }}
          target={item?.isExternal ? '_blank' : '_self'}
          _hover={{
            textDecoration: 'none'
          }}
        >
          Shop Now
        </Button>
      )}
    </Stack>
  );
};

export default ShopItem;
